import React, { useState, useEffect } from 'react';
import { 
  History, Search, Download, Loader2, 
  Filter, Calendar, User, Database, Info, X, Eye, ArrowRight
} from 'lucide-react';
import { auditLogService } from '../../services/auditLogService';
import StatusBadge from '../../components/StatusBadge';

// ===== Log Detail Modal (Proper View) =====
const LogDetailModal = ({ log, onClose }) => {
  if (!log) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 animate-in fade-in">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg text-primary">
              <History className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-black text-gray-900 leading-none">Event Details</h2>
              <p className="text-[10px] text-gray-400 font-bold uppercase mt-1 tracking-widest">ID: {log.log_id}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto space-y-6">
          {/* Metadata Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
              <p className="text-[10px] font-black text-gray-400 uppercase">Performer</p>
              <p className="text-sm font-bold text-gray-900 mt-1">{log.user_name}</p>
              <p className="text-[10px] font-medium text-primary uppercase">{log.user_role}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
              <p className="text-[10px] font-black text-gray-400 uppercase">Timestamp</p>
              <p className="text-sm font-bold text-gray-900 mt-1">{new Date(log.timestamp).toLocaleString()}</p>
            </div>
          </div>

          {/* Action & Entity */}
          <div className="flex items-center gap-3">
            <div className="px-3 py-1 bg-gray-900 text-white rounded-lg text-[10px] font-black uppercase tracking-widest">
              {log.action}
            </div>
            <div className="px-3 py-1 bg-primary text-white rounded-lg text-[10px] font-black uppercase tracking-widest">
              {log.entity}
            </div>
            <span className="text-xs font-mono text-gray-400">Ref ID: {log.entity_id}</span>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Action Description</h3>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 text-sm text-gray-700 leading-relaxed italic">
              "{log.description || 'No description provided.'}"
            </div>
          </div>

          {/* Changes Section (If data exists) */}
          {(log.old_value || log.new_value) && (
            <div className="space-y-3">
               <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Data Changes</h3>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Old Data */}
                  <div className="space-y-1.5">
                    <p className="text-[9px] font-bold text-red-400 uppercase ml-1">Previous State</p>
                    <div className="bg-red-50/30 border border-red-100 p-3 rounded-xl overflow-hidden">
                      <pre className="text-[10px] text-red-700 font-mono overflow-x-auto">
                        {log.old_value ? JSON.stringify(log.old_value, null, 2) : 'N/A'}
                      </pre>
                    </div>
                  </div>
                  {/* New Data */}
                  <div className="space-y-1.5">
                    <p className="text-[9px] font-bold text-green-500 uppercase ml-1">Updated State</p>
                    <div className="bg-green-50/30 border border-green-100 p-3 rounded-xl overflow-hidden">
                      <pre className="text-[10px] text-green-700 font-mono overflow-x-auto">
                        {log.new_value ? JSON.stringify(log.new_value, null, 2) : 'N/A'}
                      </pre>
                    </div>
                  </div>
               </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-50 bg-gray-50/30 flex justify-end">
          <button onClick={onClose} className="bg-gray-900 text-white px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all">
            Close Detail
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== Main Audit Logs Page =====
const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null); // State for Detail Modal
  const [filters, setFilters] = useState({
    entity: '',
    action: '',
    page: 1,
    page_size: 20
  });

  const ENTITIES = ['Employee', 'Contract', 'Expense', 'Lead', 'Commission', 'User', 'Schedule', 'UserInvite', 'UserActivation'];
  const ACTIONS = ['CREATE', 'UPDATE', 'DELETE', 'APPROVE', 'REJECT', 'LOGIN'];

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await auditLogService.getLogs(filters);
      setLogs(res.data?.logs || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error("Audit log load failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [filters.entity, filters.action, filters.page]);

  return (
    <div className="space-y-6 pb-10 text-gray-900">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-black text-gray-900 flex items-center gap-2">
            <History className="w-6 h-6 text-primary" /> System Audit Logs
          </h1>
          <p className="text-sm text-gray-500 font-medium">Monitor all system-wide activities and data changes</p>
        </div>
        <button 
          onClick={() => window.open(auditLogService.getExportUrl(filters.entity, filters.action), '_blank')}
          className="flex items-center gap-2 bg-white border border-gray-200 px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-gray-50 transition-all shadow-sm"
        >
          <Download className="w-4 h-4" /> Export Excel
        </button>
      </div>

      {/* Filters Bar */}
      <div className="bg-white p-4 rounded-card border border-gray-100 shadow-soft flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2 text-gray-400 mr-2">
          <Filter className="w-4 h-4" />
          <span className="text-[10px] font-black uppercase tracking-widest">Filters:</span>
        </div>
        
        <select 
          value={filters.entity}
          onChange={(e) => setFilters({...filters, entity: e.target.value, page: 1})}
          className="p-2.5 border border-gray-100 rounded-xl text-xs font-bold bg-gray-50 outline-none focus:ring-1 focus:ring-primary min-w-[150px]"
        >
          <option value="">All Entities</option>
          {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
        </select>

        <select 
          value={filters.action}
          onChange={(e) => setFilters({...filters, action: e.target.value, page: 1})}
          className="p-2.5 border border-gray-100 rounded-xl text-xs font-bold bg-gray-50 outline-none focus:ring-1 focus:ring-primary min-w-[150px]"
        >
          <option value="">All Actions</option>
          {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>

        <div className="ml-auto text-[10px] font-black text-gray-300 uppercase tracking-widest">
          {total} total events recorded
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary opacity-20 w-12 h-12" /></div>
      ) : (
        <div className="bg-white rounded-card border border-gray-100 shadow-soft overflow-hidden">
          <div className="overflow-x-auto">
            {/* Added table-fixed to control column behavior */}
            <table className="w-full text-sm text-left table-fixed min-w-[800px]">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  <th className="px-6 py-4 w-48">Timestamp</th>
                  <th className="px-6 py-4 w-56">User</th>
                  <th className="px-6 py-4 w-32">Action</th>
                  <th className="px-6 py-4 w-32">Entity</th>
                  <th className="px-6 py-4">Details (Click to view full)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                      <Info className="w-8 h-8 mx-auto mb-2 opacity-20" />
                      No system activities found for these filters.
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr 
                      key={log.id} 
                      onClick={() => setSelectedLog(log)}
                      className="hover:bg-primary/[0.02] cursor-pointer transition-colors group"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 font-mono text-[11px]">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-bold text-gray-900 leading-none">{log.user_name}</p>
                        <p className="text-[9px] text-gray-400 mt-1 uppercase font-bold tracking-tighter">{log.user_role}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={'px-2 py-0.5 rounded text-[9px] font-black uppercase ' + (
                          log.action === 'DELETE' ? 'bg-red-50 text-red-600' : 
                          log.action === 'CREATE' ? 'bg-green-50 text-green-600' : 
                          'bg-blue-50 text-blue-600'
                        )}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600 font-bold uppercase text-[10px] tracking-tight">{log.entity}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-between gap-2">
                           {/* FIXED: Using truncate to prevent horizontal stretching */}
                           <p className="text-gray-500 truncate text-xs" title={log.description}>
                             {log.description || '—'}
                           </p>
                           <Eye className="w-4 h-4 text-gray-300 group-hover:text-primary transition-colors shrink-0" />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {total > filters.page_size && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
              <button 
                disabled={filters.page === 1}
                onClick={() => setFilters({...filters, page: filters.page - 1})}
                className="px-4 py-2 text-[10px] font-black uppercase tracking-widest disabled:opacity-30 hover:text-primary transition-colors"
              >
                Previous Page
              </button>
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Page {filters.page}</span>
              <button 
                disabled={logs.length < filters.page_size}
                onClick={() => setFilters({...filters, page: filters.page + 1})}
                className="px-4 py-2 text-[10px] font-black uppercase tracking-widest disabled:opacity-30 hover:text-primary transition-colors"
              >
                Next Page
              </button>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal Render */}
      {selectedLog && (
        <LogDetailModal 
          log={selectedLog} 
          onClose={() => setSelectedLog(null)} 
        />
      )}
    </div>
  );
};

export default AuditLogs;